import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlinePrinter } from 'react-icons/ai';
import { MdOutlineAccessTime } from 'react-icons/md';
import { Link } from 'react-router-dom';

import { Icon } from '@iconify/react';

import { ArrowCircleBackground } from '../../../../assets/images/icons/arrow-circle-background';
import {
  DataTable,
  StyledButton,
  StyledDateInput,
} from '../../../../components';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import { useTransacoesTempoReal } from '../../../../services/hooks/useTransacoesTempoReal';
import { PrintComponent } from '../../../../services/PrintComponent';
import { AddLocaleDateString } from '../../../../utils/AddLocaleDateString';
import { NewFormatValue } from '../../../../utils/NewFormatValue';
import { phoebusStatusLabel } from '../../../../utils/PhoebusStatus';
import { AdminTitle } from '../../components';
import { InputDescription } from '../../components/InputDescription';
import { Container, FiltersContainer, InfoNote } from './styles';

const tableHead = [
  'Data e Hora',
  'NSU',
  'NSU Adquirente',
  'Autorização',
  'Cartão',
  'Bandeira',
  'Produto',
  'Parcelas',
  'Valor',
  'Situação',
  'Terminal',
  'Merchant',
];

export const TransacoesTempoReal = () => {
  const { documentSelected } = useUserDocument();

  const docto = useMemo(() => documentSelected as string, [documentSelected]);

  const [day, setDay] = useState<Date>(new Date());
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const [print, setPrint] = useState(false);

  const [params, setParams] = useState({
    docto: '',
    data: AddLocaleDateString(day) ?? '',
  });

  useEffect(() => {
    if (docto) {
      setParams((previousState) => ({ ...previousState, docto }));
    }
  }, [docto]);

  const { data, isLoading, isFetching, error, refetch } =
    useTransacoesTempoReal({
      ...params,
      pagina: page,
      limite: itemsPerPage,
    });

  const handleFilter = useCallback(() => {
    setParams({ docto, data: AddLocaleDateString(day) ?? '' });
    setPage(1);
    refetch();
  }, [docto, day, refetch]);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  return (
    <Container>
      <AdminTitle
        title="Transações em Tempo Real"
        icon={<MdOutlineAccessTime />}
      />

      <InfoNote>
        <Icon icon="mdi:information-outline" />
        <span>
          As transações desta página são em tempo real e, por isso, ainda não
          trazem taxas e outras informações. Para ver essas informações, acesse
          a página de <Link to="/admin/vendas">Vendas</Link>, onde os dados
          ficam disponíveis em D+1.
        </span>
      </InfoNote>

      <FiltersContainer>
        <div>
          <InputDescription placeholder="Dia da consulta" />
          <StyledDateInput
            value={day}
            onChange={setDay}
            placeholder="Dia da consulta"
            maxDate={new Date()}
          />
        </div>

        <StyledButton
          text="Consultar"
          icon={<ArrowCircleBackground />}
          iconPosition="right"
          onClick={handleFilter}
        />

        <StyledButton
          text="Imprimir"
          icon={<AiOutlinePrinter />}
          iconPosition="right"
          onClick={() => setPrint(true)}
          disabled={isLoading || isFetching}
        />
      </FiltersContainer>

      <PrintComponent onPrint={print} setOnPrint={setPrint}>
        <DataTable
          title="Detalhado"
          heads={tableHead}
          hasPagination
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          page={page}
          setPage={setPage}
          totalItems={data?.total}
          hasNextPage={data?.hasNextPage}
        >
          {isLoading ? (
            <tbody>
              <tr>
                <td colSpan={tableHead.length}>Carregando...</td>
              </tr>
            </tbody>
          ) : error ? (
            <tbody>
              <tr>
                <td colSpan={tableHead.length}>Falha ao obter dados</td>
              </tr>
            </tbody>
          ) : data?.transactions.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={tableHead.length}>Não foram encontrados dados</td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {data?.transactions.map((transacao, index) => (
                <tr key={transacao.uuid ?? `${transacao.nsu}-${index}`}>
                  <td>
                    {transacao.start_date
                      ? new Date(transacao.start_date).toLocaleString('pt-BR')
                      : '-'}
                  </td>
                  <td>{transacao.nsu}</td>
                  <td>{transacao.acquirer_nsu}</td>
                  <td>{transacao.authorization_number}</td>
                  <td>{transacao.card_number}</td>
                  <td>{transacao.brand}</td>
                  <td>{transacao.product_name}</td>
                  <td>{transacao.parcels}</td>
                  <td>
                    {transacao.value !== undefined
                      ? NewFormatValue(Number(transacao.value))
                      : '-'}
                  </td>
                  <td>{phoebusStatusLabel(transacao.status)}</td>
                  <td>{transacao.terminal}</td>
                  <td>{transacao.merchant}</td>
                </tr>
              ))}
            </tbody>
          )}
        </DataTable>
      </PrintComponent>
    </Container>
  );
};
